import React, { useEffect, useState } from 'react';
import { useConfigContext } from '../../shared/context/ConfigContext';
import { Box } from '@mui/material';
import {
  Card,
  CardContent,
  CardHeader,
  Inline,
  Typography,
  Tooltip,
} from '@airbus/components-react';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import styles from './DisplayScreens.module.css';
import { ScreenData } from '../../shared/types';
import { postRequest } from '../../shared/utils';
import { useNavigate } from 'react-router';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface DisplayScreenProps {
  selectedTab: number;
  screenAccessDetails: ScreenData;
  noScreenAccessDetails: ScreenData;
}

export function useScreenDescriptions() {
  const { configSchema } = useConfigContext();
  const [descMap, setDescMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!configSchema) return;

    setLoading(true);
    setError(null);

    const body = {
      schema: configSchema,
      table: 'mbd_screens',
      columns: ['id', 'description'],
    };

    postRequest('/api/records', body)
      .then((result) => {
        if (result.errors) throw new Error('Failed to fetch screen descriptions');
        const records = result.data?.[`${configSchema}_mbd_screens`] || [];
        const mapped = {};
        records.forEach((row) => {
          mapped[row.id] = row.description;
        });
        setDescMap(mapped);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [configSchema]);

  return { descMap, loading, error };
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className={`${styles.displaytab} displaytab`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const sortScreensFn = (screenA: ScreenData[number], screenB: ScreenData[number]) => {
  const textA = screenA.screenName.toUpperCase();
  const textB = screenB.screenName.toUpperCase();
  return textA > textB === true ? 1 : -1;
};

const DisplayScreens = (props: DisplayScreenProps) => {
  const { selectedTab, screenAccessDetails, noScreenAccessDetails } = props;
  const [adminApps] = useState(['NPI Tool Administration']);
  const navigate = useNavigate();

  const descriptions = useScreenDescriptions();

  const businessAccessScreens = screenAccessDetails
    ?.filter((screenData) => !adminApps.includes(screenData.parentAppName))
    ?.sort(sortScreensFn)
    ?.map((screenData) => (
      <Tooltip
        key={screenData.id}
        label={screenData.description}
        arrow
        style={{ maxWidth: '25rem' }}
      >
        <Card
          key={screenData.id}
          className={`${styles.cardblock} cardblock`}
          onClick={() => navigate(`mbdcp/${screenData.appNameId}`)}
        >
          <CardHeader icon={<AirplanemodeActiveIcon />} title={screenData.screenName} />
        </Card>
      </Tooltip>
    ));

  const adminAccessScreens = screenAccessDetails
    ?.filter((screenData) => adminApps.includes(screenData.parentAppName))
    ?.sort(sortScreensFn)
    ?.map((screenData) => {
      return (
        <Tooltip
          key={screenData.id}
          label={descriptions[screenData.screenName] || screenData.screenName}
          arrow
        >
          <Card
            key={screenData.id}
            className={`${styles.cardblock} cardblock`}
            onClick={() => navigate(`admin/${screenData.appNameId}`)}
          >
            <CardHeader icon={<AirplanemodeActiveIcon />} title={screenData.screenName} />
            <CardContent></CardContent>
          </Card>
        </Tooltip>
      );
    });

  const businessNoAccessScreens = noScreenAccessDetails
    ?.filter((screenData) => !adminApps.includes(screenData.parentAppName))
    ?.sort(sortScreensFn)
    ?.map((screenData) => {
      return (
        <Tooltip
          key={screenData.id}
          label={descriptions[screenData.screenName] || screenData.screenName}
          arrow
        >
          <Card key={screenData.id} className={styles.dimCard}>
            <CardHeader icon={<AirplanemodeActiveIcon />} title={screenData.screenName} />
            <CardContent></CardContent>
          </Card>
        </Tooltip>
      );
    });

  const adminNoAccessScreens = noScreenAccessDetails
    ?.filter((screenData) => adminApps.includes(screenData.parentAppName))
    ?.sort(sortScreensFn)
    ?.map((screenData) => {
      return (
        <Tooltip
          key={screenData.id}
          label={descriptions[screenData.screenName] || screenData.screenName}
          arrow
        >
          <Card key={screenData.id} className={styles.dimCard}>
            <CardHeader icon={<AirplanemodeActiveIcon />} title={screenData.screenName} />
            <CardContent></CardContent>
          </Card>
        </Tooltip>
      );
    });

  return (
    <div>
      {/* Business Screens */}
      <CustomTabPanel value={selectedTab} index={0}>
        <Typography variant='h6'>{`Business Screens`}</Typography>
        <Inline wrap spacing='2-x'>
          {businessAccessScreens}
          {businessNoAccessScreens}
        </Inline>
      </CustomTabPanel>
      {/* Admin Screens */}
      {adminAccessScreens.length > 0 && (
        <CustomTabPanel value={selectedTab} index={0}>
          <Typography variant='h6'>{`Admin Screens`}</Typography>
          <Inline wrap spacing='2-x'>
            {adminAccessScreens}
            {adminNoAccessScreens}
          </Inline>
        </CustomTabPanel>
      )}
    </div>
  );
};

export default DisplayScreens;
